import { Textarea, TextInput } from '@any-ui-react/core';
import { differenceInYears, subYears } from 'date-fns';
import type { CountryCode } from 'libphonenumber-js';
import { useEffect, useMemo } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';

import { PhoneNumberInput } from '@/common/components/employees/phone-number-input/PhoneNumberInput';
import { DatePicker } from '@/common/components/shared/date-picker/DatePicker';
import { CountrySelectSingle } from '@/common/components/shared/select/country/CountrySelect';
import { GenderSelectSingle } from '@/common/components/shared/select/gender/GenderSelect';
import { useEmployeeDetailContext } from '@/common/contexts/EmployeeDetailContext';
import { ContractType } from '@/common/graphql';
import { type EmployeeAddFormDataType, type EmployeePersonalInformationFormDataType, FormAction } from '@/common/types';
import { DateUtils, StringUtils } from '@/common/utils';

interface Props {
  mode: FormAction;
}

export const PersonalInformationFormFields = ({ mode }: Props) => {
  const {
    formState: { errors },
    register,
    setValue,
    clearErrors,
    control,
  } = useFormContext<EmployeePersonalInformationFormDataType>();
  const { control: controlForAdd } = useFormContext<EmployeeAddFormDataType>();

  const gender = useWatch({ control, name: 'gender' });
  const dateOfBirth = useWatch({ control, name: 'dateOfBirth' });
  const personalPhoneNumber = useWatch({ control, name: 'personalPhoneNumber' });
  const personalPhoneNumberCountryCode = useWatch({ control, name: 'personalPhoneNumberCountryCode' });

  // Get employee detail context
  const { employeeDetail } = useEmployeeDetailContext();

  // Get company and contract information
  const formCompanyCountryId = useWatch({ control: controlForAdd, name: 'companyCountryId' }) as CountryCode;
  const formContractType = useWatch({ control: controlForAdd, name: 'contractType' });

  // For edit mode, get from employee detail; for create mode, get from form values
  const companyCountryId = useMemo(
    () =>
      mode === FormAction.CREATING
        ? formCompanyCountryId
        : (employeeDetail?.generalInformation?.companyCountryId as CountryCode),
    [mode, formCompanyCountryId, employeeDetail],
  );
  const contractType = useMemo(
    () => (mode === FormAction.CREATING ? formContractType : employeeDetail?.generalInformation?.contractType),
    [mode, formContractType, employeeDetail],
  );

  const isDateOfBirthOptional = useMemo(
    () => companyCountryId === 'JP' && contractType === ContractType.Contractor,
    [companyCountryId, contractType],
  );
  const maxDateOfBirth = useMemo(
    () =>
      subYears(
        employeeDetail.generalInformation.hiredDate
          ? new Date(employeeDetail.generalInformation.hiredDate)
          : new Date(),
        15,
      ),
    [employeeDetail.generalInformation.hiredDate],
  );

  useEffect(() => {
    if (mode === FormAction.CREATING && companyCountryId) {
      setValue('personalPhoneNumberCountryCode', companyCountryId, { shouldDirty: true });
      setValue('residentialCountryId', companyCountryId, { shouldDirty: true });
    }
  }, [companyCountryId, mode, setValue]);

  return (
    <fieldset className="form-fieldset">
      <div className="col-span-2 sm:col-span-1">
        <GenderSelectSingle
          error={errors.gender?.message}
          label="Gender"
          placeholder="Select Gender"
          required={true}
          value={gender}
          onChange={value => {
            setValue('gender', value?.value ?? null, { shouldDirty: true });
            clearErrors('gender');
          }}
        />
      </div>

      <div className="col-span-2 sm:col-span-1">
        <DatePicker
          clearable={isDateOfBirthOptional}
          defaultDate={DateUtils.formatDate(subYears(new Date(), 22))}
          error={errors.dateOfBirth?.message}
          label="Date of Birth"
          maxDate={maxDateOfBirth}
          placeholder="yyyy-MM-dd"
          required={!isDateOfBirthOptional}
          value={dateOfBirth ?? undefined}
          onChange={value => {
            if (value) {
              setValue('dateOfBirth', DateUtils.formatDate(value), { shouldDirty: true });
              setValue('age', differenceInYears(new Date(), new Date(value)));
            }
            clearErrors('dateOfBirth');
          }}
        />

        {dateOfBirth && (
          <Controller
            control={control}
            name="age"
            render={({ field: { value } }) => {
              if (!value || !DateUtils.parseDate(value)) {
                return <div />;
              }

              return <div className="text-gray-7 text-xs ml-3">{StringUtils.withPluralize(value, 'year')} old</div>;
            }}
          />
        )}
      </div>
      <div className="col-span-2 sm:col-span-1">
        <PhoneNumberInput
          {...register('personalPhoneNumber')}
          preferredCountryCode={companyCountryId || undefined}
          defaultValue={{
            countryCode: (personalPhoneNumberCountryCode as CountryCode) || '',
            number: personalPhoneNumber || null,
          }}
          error={errors.personalPhoneNumber?.message || errors.personalPhoneNumberCountryCode?.message}
          label="Personal Phone number"
          onChange={({ number, countryCode, isValid }) => {
            setValue('personalPhoneNumber', number, { shouldDirty: true });
            if (countryCode) {
              setValue('personalPhoneNumberCountryCode', countryCode, { shouldDirty: true });
            }

            if (isValid || !number) {
              clearErrors('personalPhoneNumber');
              clearErrors('personalPhoneNumberCountryCode');
            }
          }}
        />
      </div>

      <TextInput
        {...register('personalEmail')}
        autoComplete="off"
        className="col-span-2 sm:col-span-1"
        data-1p-ignore={true}
        error={errors.personalEmail?.message}
        label="Personal Email"
        placeholder="user@gmail.com"
      />

      <Controller
        control={control}
        name="residentialCountryId"
        render={({ field: { ...field } }) => (
          <div className="col-span-2 sm:col-span-1">
            <CountrySelectSingle
              {...field}
              error={errors.residentialCountryId?.message}
              label="Residential Location"
              placeholder="Select Residential Location"
              required={true}
              onChange={option => {
                setValue('residentialCountryId', option?.value as string, { shouldDirty: true });
              }}
            />
          </div>
        )}
      />
      <div className="hidden sm:block col-span-1" />

      <div className="col-span-2 col-start-1 sm:col-span-1">
        <Textarea
          {...register('currentAddress')}
          autosize={true}
          className="w-full"
          label="Current Address"
          minRows={5}
          placeholder="106-6131 Roppongi Hills 31F, Roppongi, Minato-ku, Tokyo"
        />
      </div>
      <div className="col-span-2 col-start-1 sm:col-span-1">
        <Textarea
          {...register('permanentAddress')}
          autosize={true}
          className="w-full"
          label="Permanent Address"
          minRows={5}
          placeholder="106-6131 Roppongi Hills 31F, Roppongi, Minato-ku, Tokyo"
        />
      </div>
    </fieldset>
  );
};
